import moment from 'moment';
import {
	MONTH_DAY_WIDTH,
	QUARTER_DAY_WIDTH,
	HALF_YEAR_DAY_WIDTH,
	PERIODS,
	DAYS_IN_WEEK,
} from '../constants';

export const getMinMaxInitiativesDates = initiatives => {
	const minDate = new Date(
		Math.min.apply(
			null,
			initiatives.map(i => new Date(i.executionStartDate).getTime())
		)
	);
	const maxs = initiatives.map(i =>
		moment(i.executionStartDate)
			.add(i.duration, 'week')
			.valueOf()
	);
	const maxDate = new Date(Math.max.apply(null, maxs));
	return { minDate, maxDate };
};

export const getDayWidth = period => {
	if (period === PERIODS[0]) {
		return MONTH_DAY_WIDTH;
	}
	if (period === PERIODS[1]) {
		return QUARTER_DAY_WIDTH;
	}
	if (period === PERIODS[2]) {
		return HALF_YEAR_DAY_WIDTH;
	}
	return MONTH_DAY_WIDTH;
};

export const getHeaders = (minDate, maxDate, period) => {
	if (period === PERIODS[0]) {
		return getHeaderForMonth(minDate, maxDate);
	}
	if (period === PERIODS[1]) {
		return getHeaderForQuarter(minDate, maxDate);
	}
	if (period === PERIODS[2]) {
		return getHeaderForHalfYear(minDate, maxDate);
	}
	return [];
};

export const getAdditionalHeader = (headers, period) => {
	const lastHeader =
		headers && headers.length ? headers[headers.length - 1] : null;
	if (!lastHeader) {
		return [];
	}

	if (period === PERIODS[0]) {
		const minDate = moment(lastHeader.dateStart).add(1, 'month');
		const maxDate = moment(lastHeader.dateStart).add(2, 'months');

		return [...headers, ...getHeaderForMonth(minDate, maxDate)];
	}
	if (period === PERIODS[1]) {
		const minDate = moment(lastHeader.dateStart).add(3, 'months');
		const maxDate = moment(lastHeader.dateStart).add(6, 'months');

		return [...headers, ...getHeaderForQuarter(minDate, maxDate)];
	}
	if (period === PERIODS[2]) {
		const minDate = moment(lastHeader.dateStart).add(6, 'months');
		const maxDate = moment(lastHeader.dateStart).add(12, 'months');

		return [...headers, ...getHeaderForHalfYear(minDate, maxDate)];
	}
	return headers;
};

export const getPeriods = (minDate, maxDate, period) => {
	if (period === PERIODS[0]) {
		return getPeriodForMonth(minDate, maxDate);
	}
	if (period === PERIODS[1]) {
		return getPeriodForQuarter(minDate, maxDate);
	}
	if (period === PERIODS[2]) {
		return getPeriodForQuarter(minDate, maxDate);
	}
	return [];
};

export const getParametersForRange = ({ minDate, startDate, endDate }) => {
	const momentDuration = moment.duration(
		moment(endDate).diff(moment(startDate))
	);
	const durationDays = Math.round(momentDuration.asDays());
	const durationWeeks = Math.round(momentDuration.asWeeks());
	return {
		left: moment(startDate).diff(moment(minDate), 'days'),
		durationDays,
		durationWeeks,
	};
};

export const isPlural = (text, amount) => (amount <= 1 ? text : `${text}s`);

const getHeaderForMonth = (minDate, maxDate) => {
	const dateStart = moment(minDate);
	const dateEnd = moment(maxDate);
	let timeValues = [];

	while (dateEnd > dateStart || dateStart.format('M') === dateEnd.format('M')) {
		let endOfPeriod = moment(dateStart).endOf('month');
		if (endOfPeriod.diff(dateEnd) > 0) {
			endOfPeriod = dateEnd;
		}
		timeValues.push({
			title: dateStart.format('MMMM'),
			value: dateStart.format('YYYY'),
			duration: Math.round(
				moment.duration(endOfPeriod.diff(dateStart)).asDays()
			),
			dateStart: moment(dateStart),
		});
		dateStart.add(1, 'month');
	}
	return timeValues.filter(t => t.duration);
};

const getHeaderForQuarter = (minDate, maxDate) => {
	const dateStart = moment(minDate);
	const dateEnd = moment(maxDate);
	let timeValues = [];

	while (dateEnd > dateStart || dateStart.format('M') === dateEnd.format('M')) {
		let endOfPeriod = moment(dateStart).add(3, 'month');
		if (endOfPeriod.diff(dateEnd) > 0) {
			endOfPeriod = dateEnd;
		}
		timeValues.push({
			title: `Q${dateStart.format('Q')}`,
			value: dateStart.format('YYYY'),
			duration: Math.round(
				moment.duration(endOfPeriod.diff(dateStart)).asDays()
			),
			dateStart: moment(dateStart),
		});
		dateStart.add(3, 'month');
	}
	return timeValues.filter(t => t.duration);
};

const getHeaderForHalfYear = (minDate, maxDate) => {
	const dateStart = moment(minDate);
	const dateEnd = moment(maxDate);
	let timeValues = [];

	while (dateEnd > dateStart || dateStart.format('M') === dateEnd.format('M')) {
		let endOfPeriod = moment(dateStart).add(6, 'month');
		if (endOfPeriod.diff(dateEnd) > 0) {
			endOfPeriod = dateEnd;
		}
		timeValues.push({
			title: `H${
				dateStart.diff(
					moment(dateStart)
						.startOf('year')
						.add(6, 'month')
				) > 0
					? 2
					: 1
			}`,
			value: dateStart.format('YYYY'),
			duration: Math.round(
				moment.duration(endOfPeriod.diff(dateStart)).asDays()
			),
			dateStart: moment(dateStart),
		});
		dateStart.add(6, 'month');
	}
	return timeValues.filter(t => t.duration);
};

const getPeriodForMonth = (minDate, maxDate) => {
	let monday = moment(minDate).startOf('isoWeek');
	if (monday.isBefore(moment(minDate))) {
		monday = monday.add(DAYS_IN_WEEK, 'days').startOf('isoWeek');
	}
	const lastDay = moment(maxDate);
	let mondays = [];

	while (monday.isBefore(lastDay)) {
		mondays.push({
			left: monday.diff(moment(minDate), 'days'),
			periodLeft: monday.diff(moment(minDate), 'days'),
			title: monday.date(),
			duration: 7,
		});
		monday.add(DAYS_IN_WEEK, 'days');
	}
	return mondays;
};

const getPeriodForQuarter = (minDate, maxDate) => {
	const dateStart = moment(minDate);
	const dateEnd = moment(maxDate);
	let timeValues = [];

	while (dateEnd > dateStart || dateStart.format('M') === dateEnd.format('M')) {
		let endOfPeriod = moment(dateStart).endOf('month');
		if (endOfPeriod.diff(dateEnd) > 0) {
			endOfPeriod = dateEnd;
		}
		const left = dateStart.diff(moment(minDate), 'days');
		const duration = Math.round(
			moment.duration(endOfPeriod.diff(dateStart)).asDays()
		);
		timeValues.push({
			left,
			periodLeft: left + duration,
			title: dateStart.format('MMMM'),
			duration,
		});
		dateStart.add(1, 'month');
	}

	return timeValues;
};

export const getStartOfPeriod = (date, period) => {
	if (!period) {
		return date;
	}
	if (period === PERIODS[2]) {
		const momentDate = moment(date);
		if (momentDate.month() < 5) {
			return momentDate.startOf('year');
		}
		return momentDate.startOf('year').add(6, 'month');
	}
	return moment(date).startOf(period.toLowerCase());
};

export const getEndOfPeriod = (date, period) => {
	if (!period) {
		return date;
	}
	if (period === PERIODS[2]) {
		const momentDate = moment(date);

		if (momentDate.month() > 5) {
			return momentDate.endOf('year');
		}
		return momentDate.startOf('year').add(6, 'month');
	}
	return moment(date)
		.add(1, 'week')
		.endOf(period.toLowerCase());
};

export const getInitiativeLabelText = (duration, width) => {
	if (width < HALF_YEAR_DAY_WIDTH * 3 * DAYS_IN_WEEK) {
		return duration;
	} else if (width < HALF_YEAR_DAY_WIDTH * 5 * DAYS_IN_WEEK) {
		return `${duration} w`;
	}
	return `${duration} ${isPlural('week', duration)}`;
};
